// src/components/block/tasksManagment/TaskManager.tsx
//
// Généralisation d'UploadManager : le même bandeau, mais pour toute tâche de
// fond — les uploads (store fileManagment, inchangé) ET les transcodages
// (store tasksManagment, alimenté par MediaRow.tsx pendant qu'il sonde la
// progression pour sa propre ligne). Une tâche de transcodage ne vit donc que
// tant qu'au moins une ligne de média l'a poussée dans ce store — si on quitte
// la bibliothèque de médias avant la fin, elle disparaît du bandeau (elle
// réapparaît en revanche dès qu'on rouvre la ligne, avec la progression déjà
// avancée par le serveur).

import React, { useState } from 'react';
import { useTypedSelector, useTypedDispatch } from '../../../store/useTypeSelector';
import {
  getAllUploads,
  removeUpload,
} from '../../../store/fileManagment/actions';
import { UploadStatus, UploadState } from '../../../store/fileManagment/types';
import {
  getAllTranscodingTasks,
  removeTranscodingTask,
} from '../../../store/tasksManagment/actions';
import { TranscodingTaskState, TranscodingTaskStatus } from '../../../store/tasksManagment/types';
import { clearUploadResumeState } from '../../../backendServices/fileManagment/api';

type TaskItem =
  | { kind: 'upload'; id: string; data: UploadState }
  | { kind: 'transcoding'; id: string; data: TranscodingTaskState };

const isTaskActive = (task: TaskItem): boolean =>
  task.kind === 'upload'
    ? [UploadStatus.UPLOADING, UploadStatus.PENDING, UploadStatus.PAUSED, UploadStatus.RETRYING].includes(task.data.status)
    : task.data.status === TranscodingTaskStatus.TRANSCODING;

const isTaskFailed = (task: TaskItem): boolean =>
  task.kind === 'upload' ? task.data.status === UploadStatus.FAILED : task.data.status === TranscodingTaskStatus.FAILED;

const isTaskCompleted = (task: TaskItem): boolean =>
  task.kind === 'upload' ? task.data.status === UploadStatus.COMPLETED : task.data.status === TranscodingTaskStatus.COMPLETED;

const isTaskRemovable = (task: TaskItem): boolean =>
  task.kind === 'upload'
    ? [UploadStatus.COMPLETED, UploadStatus.CANCELLED, UploadStatus.FAILED].includes(task.data.status)
    : task.data.status !== TranscodingTaskStatus.TRANSCODING;

const taskLabel = (task: TaskItem): string => (task.kind === 'upload' ? task.data.fileName : task.data.label);
const taskProgress = (task: TaskItem): number => (task.kind === 'upload' ? task.data.progress : task.data.progress);

const taskKindLabel = (task: TaskItem): string => (task.kind === 'upload' ? 'Upload' : 'Transcodage');

const uploadStatusLabels: Record<UploadStatus, string> = {
  [UploadStatus.UPLOADING]: 'En cours',
  [UploadStatus.PENDING]:   'En attente',
  [UploadStatus.PAUSED]:    'En pause',
  [UploadStatus.COMPLETED]: 'Terminé',
  [UploadStatus.FAILED]:    'Erreur',
  [UploadStatus.CANCELLED]: 'Annulé',
  [UploadStatus.RETRYING]:  'Réessai',
};

const transcodingStatusLabels: Record<TranscodingTaskStatus, string> = {
  [TranscodingTaskStatus.TRANSCODING]: 'En cours',
  [TranscodingTaskStatus.COMPLETED]:   'Terminé',
  [TranscodingTaskStatus.FAILED]:      'Erreur',
};

const taskStatusLabel = (task: TaskItem): string =>
  task.kind === 'upload' ? uploadStatusLabels[task.data.status] : transcodingStatusLabels[task.data.status];

/** `xh ymin zs`, chaque unité de tête omise si elle est nulle. */
const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}min ${s}s`;
  if (m > 0) return `${m}min ${s}s`;
  return `${s}s`;
};

/** Mo, ou Go au-delà de 1000 Mo. */
const formatSizeMo = (bytes: number): string => {
  const mo = bytes / (1024 * 1024);
  if (mo > 1000) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
  return `${mo.toFixed(1)} Mo`;
};

const taskDurationSeconds = (task: TaskItem): number | null =>
  task.data.endTime ? (task.data.endTime.getTime() - task.data.startTime.getTime()) / 1000 : null;

/** Résumé d'une tâche terminée, à la place de la jauge — plus de pourcentage à
 * montrer une fois le travail fini. */
const completedSummary = (task: TaskItem): string => {
  const duree = taskDurationSeconds(task);

  if (task.kind === 'transcoding') {
    return duree !== null ? `Transcodé en ${formatDuration(duree)}` : 'Transcodé';
  }

  const taille = formatSizeMo(task.data.fileSize);
  if (duree === null || duree <= 0) return `${taille} téléversé`;

  const mbps = (task.data.fileSize * 8) / duree / 1_000_000;
  return `${taille} téléversé en ${formatDuration(duree)} avec une vitesse moyenne de ${mbps.toFixed(1)} Mbit/s`;
};

const statusColor = (task: TaskItem): string => {
  if (isTaskFailed(task))    return '#ef4444';
  if (isTaskCompleted(task)) return '#10b981';
  if (task.kind === 'upload' && task.data.status === UploadStatus.PAUSED) return '#f59e0b';
  if (task.kind === 'upload' && task.data.status === UploadStatus.PENDING) return '#9ca3af';
  return '#3b82f6';
};

const TaskManager = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showWhenEmpty] = useState(false);

  const uploads          = useTypedSelector(getAllUploads);
  const transcodingTasks = useTypedSelector(getAllTranscodingTasks);

  const dispatch = useTypedDispatch();

  // Plus récent au plus ancien : `startTime` est figé à la première apparition
  // de la tâche (cf. reducer tasksManagment), donc l'ordre reste stable tant
  // qu'elle vit dans le bandeau.
  const tasks: TaskItem[] = [
    ...uploads.map((u): TaskItem => ({ kind: 'upload', id: `upload:${u.id}`, data: u })),
    ...transcodingTasks.map((t): TaskItem => ({ kind: 'transcoding', id: `transcoding:${t.mediaId}`, data: t })),
  ].sort((a, b) => b.data.startTime.getTime() - a.data.startTime.getTime());

  const activeTasks    = tasks.filter(isTaskActive);
  const completedCount = tasks.filter(isTaskCompleted).length;
  const failedCount    = tasks.filter(isTaskFailed).length;

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getProgressBarColor = (task: TaskItem): string => {
    if (isTaskFailed(task))    return '#ef4444';
    if (isTaskCompleted(task)) return '#10b981';
    if (task.kind === 'upload' && task.data.status === UploadStatus.PAUSED) return '#f59e0b';
    return '#3b82f6';
  };

  // Retirer un upload retire aussi l'état de reprise laissé dans localStorage par un
  // upload échoué : sans ça, la clé `file-upload-<id>` survivrait à la ligne qui la
  // désignait. Un transcodage n'a pas cet état à nettoyer.
  const handleRemove = (task: TaskItem) => {
    if (task.kind === 'upload') {
      clearUploadResumeState(task.data.id);
      dispatch(removeUpload(task.data.id));
    } else {
      dispatch(removeTranscodingTask(task.data.mediaId));
    }
  };

  // Les échecs comptent autant que les tâches actives pour décider d'afficher le
  // bouton. Une tâche en échec n'est plus « active » : ne regarder que activeTasks
  // faisait disparaître le bouton à l'instant précis où quelque chose venait de mal
  // tourner, c'est-à-dire au seul moment où il avait quelque chose à dire.
  if (activeTasks.length === 0 && failedCount === 0 && !showWhenEmpty) {
    return null;
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {/* Bouton principal */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          // Le rouge l'emporte : un échec est ce qui appelle une action de
          // l'utilisateur, même s'il reste d'autres tâches en cours à côté.
          backgroundColor: failedCount > 0 ? '#ef4444' : activeTasks.length === 0 ? '#e5e7eb' : '#3b82f6',
          color: failedCount > 0 || activeTasks.length > 0 ? 'white' : '#374151',
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          cursor: 'pointer',
          fontWeight: '500',
          fontSize: '14px',
          transition: 'background-color 0.2s'
        }}
      >
        <span>
          {activeTasks.length > 0
            ? `${activeTasks.length} tâche${activeTasks.length > 1 ? 's' : ''}${failedCount > 0 ? ` · ${failedCount} échec${failedCount > 1 ? 's' : ''}` : ''}`
            : failedCount > 0
              ? `${failedCount} tâche${failedCount > 1 ? 's' : ''} en échec`
              : 'Aucune tâche en cours'
          }
        </span>
        {(activeTasks.length > 0 || failedCount > 0) && (
          <span style={{ fontSize: '18px' }}>
            {isOpen ? '▼' : '▲'}
          </span>
        )}
      </button>

      {/* Panel déroulant. Même condition que le bouton : sans le cas « échec », le panneau
          restait vide juste après une panne, alors que c'est là qu'on l'ouvre. */}
      {isOpen && (activeTasks.length > 0 || failedCount > 0) && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: '8px',
          // Assez large pour que le résumé d'un téléversement terminé tienne sur
          // une seule ligne (« X Mo téléversé en Xh Ymin Zs avec une vitesse
          // moyenne de W Mbit/s »).
          width: '500px',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          zIndex: 50
        }}>
          {/* En-tête */}
          <div style={{
            backgroundColor: '#f0f9ff',
            padding: '16px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h3 style={{ fontWeight: '600', color: '#1f2937', margin: '0 0 8px 0' }}>
              Gestion des tâches
            </h3>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {activeTasks.length} en cours · {completedCount} terminées {failedCount > 0 && `· ${failedCount} erreurs`}
            </div>
          </div>

          {/* Liste des tâches */}
          <div style={{
            maxHeight: '384px',
            overflowY: 'auto'
          }}>
            {tasks.map((task) => (
              <div
                key={task.id}
                style={{
                  padding: '10px 16px',
                  borderBottom: '1px solid #f3f4f6',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {/* Info tâche. Pause / Reprendre / Annuler / Réessayer n'ont pas leur
                    place ici : ils ne dispatchaient qu'un statut AFFICHÉ pendant que
                    les morceaux continuaient de partir — cf. UploadManager, dont ce
                    composant reprend le choix. Un transcodage a son propre
                    Réessayer/Annuler sur la ligne du média (MediaRow.tsx) ; ici, la
                    corbeille ne retire que la ligne de cette liste. */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isTaskActive(task) ? '6px' : 0 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontWeight: '500', color: '#1f2937', margin: 0, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {taskLabel(task)}
                    </p>
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
                      {taskKindLabel(task)}
                      {task.kind === 'upload' && ` · ${formatBytes(task.data.fileSize)}`}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '8px', flexShrink: 0 }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: statusColor(task) }}>
                      {taskStatusLabel(task)}
                    </span>
                    {isTaskRemovable(task) && (
                      <button
                        onClick   = {() => handleRemove(task)}
                        title     = "Supprimer"
                        aria-label= "Supprimer"
                        style={{
                          display        : 'flex',
                          alignItems     : 'center',
                          justifyContent : 'center',
                          padding        : '3px 6px',
                          backgroundColor: '#f3f4f6',
                          border         : 'none',
                          borderRadius   : '4px',
                          cursor         : 'pointer',
                          fontSize       : '18px',
                          lineHeight     : 1,
                          color          : '#4b5563',
                        }}
                      >
                        🗑
                      </button>
                    )}
                  </div>
                </div>

                {/* En cours : rien à montrer sous la jauge, donc rien après elle. */}
                {isTaskActive(task) && (
                  <div style={{
                    width          : '100%',
                    backgroundColor: '#e5e7eb',
                    borderRadius   : '9999px',
                    height         : '8px',
                    overflow       : 'hidden',
                  }}>
                    <div
                      style={{
                        height        : '100%',
                        backgroundColor: getProgressBarColor(task),
                        width         : `${taskProgress(task)}%`,
                        transition    : 'width 0.3s',
                      }}
                    />
                  </div>
                )}

                {/* Terminé : un résumé plutôt qu'une jauge — voir completedSummary. */}
                {isTaskCompleted(task) && (
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                    {completedSummary(task)}
                  </p>
                )}

                {/* Échec : la jauge reste, elle dit jusqu'où c'est allé. */}
                {isTaskFailed(task) && (
                  <div style={{
                    width          : '100%',
                    backgroundColor: '#e5e7eb',
                    borderRadius   : '9999px',
                    height         : '8px',
                    overflow       : 'hidden',
                  }}>
                    <div
                      style={{
                        height        : '100%',
                        backgroundColor: getProgressBarColor(task),
                        width         : `${taskProgress(task)}%`,
                        transition    : 'width 0.3s',
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskManager;
